
import React, { useState, useContext, useEffect } from 'react'
import {
	Button,
	Card,
	CardBody,
	CardHeader,
	CardFooter,
	Col,
	Container,
	FormGroup,
	Input,
	Label,
	Modal,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Row,
	Table,
} from 'reactstrap'
import { useTranslation } from 'react-i18next'
import { SpinnerComponent } from 'react-element-spinner'
import { library } from "@fortawesome/fontawesome-svg-core"
import { faScroll, faTruckRampBox, faPeopleArrows, faReceipt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

library.add(faScroll, faTruckRampBox, faPeopleArrows, faReceipt)

import VehiclesService from '../../../services/VehiclesService'
import { SessionContext } from '../../../contexts/SessionProvider'
import Paginator from '../../../components/Paginator/Paginator'
import PageTitleContextMenu from '../../../components/PageTitles/PageTitleContextMenu'
import SiteService from '../../../services/SiteService'
import {subscribe, trigger, unsubscribe} from '../../../events/customEvents'
import PdfViewerModal from '../../../components/Modals/PdfViewerModal'
import DocumentsService from '../../../services/DocumentsService'
import Enumeration from '../../../enumeration'

const DealerOwnedVehicleListing = () =>
{
	const [isBusy, setIsBusy] = useState(false)
	const { t } = useTranslation()
	const [pagingResult, setPagingResult] = useState({ pageCollection: [], totalRecords: 0 })
	// Default the date filter to the last 30 days so the Owned Vehicles list opens
	// already scoped to recent purchases (per QA feedback - May 2026).
	const formatYmd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
	const today = new Date()
	const thirtyDaysAgo = new Date()
	thirtyDaysAgo.setDate(today.getDate() - 30)
	const [startDate, setStartDate] = useState(formatYmd(thirtyDaysAgo))
	const [endDate, setEndDate] = useState(formatYmd(today))
	const [pagingRequest, setPagingRequest] = useState(	{ pageIndex: 0, pageSize: 15, startDate: thirtyDaysAgo.toISOString(), endDate: today.toISOString() })
	const sessionContext = useContext(SessionContext)
	const [showArbitrationModal, setShowArbitrationModal] = useState(false)
	const [showPdfViewerModal, setShowPdfViewerModal] = useState(false)
	const [confirmationOfSaleBase64, setConfirmationOfSaleBase64] = useState(undefined)
	const [pdfBlob, setPdfBlob] = useState(undefined)
	
	useEffect(() =>
	{
		retrieveOwnedVehicles(pagingRequest)
		
		subscribe('pdfViewerModalClosed', handlePdfViewerModalClosed)
	}, [])
	
	useEffect(() =>
	{
		return () =>
		{
			unsubscribe('pdfViewerModalClosed', handlePdfViewerModalClosed)
		}
	}, [])
	
	const handlePdfViewerModalClosed = (event) =>
	{
		setShowPdfViewerModal(false)
	}
	
	const retrieveOwnedVehicles = (pagingRequest) =>
	{
		setIsBusy(true)
		VehiclesService
			.getPagedOwnedVehicles(pagingRequest, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)
				
				setPagingResult(result.data.actionResult)
			})
			.catch((error) =>
			{
				setIsBusy(false)
				SiteService.displayApiError(error)
			})
	}
	
	const handleReload = () =>
	{
		retrieveOwnedVehicles(pagingRequest)
	}

	const handleApplyDateFilter = () =>
	{
		// Build inclusive UTC bounds: start = midnight of selected start day,
		// end = end-of-day of selected end day so a single-day range still returns rows.
		const startIso = startDate ? new Date(`${startDate}T00:00:00`).toISOString() : null
		const endIso = endDate ? new Date(`${endDate}T23:59:59.999`).toISOString() : null
		const next = { ...pagingRequest, pageIndex: 0, startDate: startIso, endDate: endIso }
		setPagingRequest(next)
		retrieveOwnedVehicles(next)
	}
	
	const handlePageChange = () =>
	{
	
	}
	
	const handleRequestOwnership = (ownedVehicle) =>
	{
		setIsBusy(true)
		VehiclesService
			.addOwnershipRequest(ownedVehicle.vehicleId, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)
				
				trigger('raiseNotification', { title: 'Request Success', message: 'The ownership request was submitted.', type: 'success', duration: 5 })
				
				setPagingResult((previousPagingResult) =>
				{
					let currentPagingResult = previousPagingResult
					let pageItem = currentPagingResult.pageCollection.find((collectionItem) => collectionItem.vehicleId === ownedVehicle.vehicleId)
					if (pageItem)
					{
						pageItem.hasOwnershipRequest = true
					}
					return currentPagingResult
				})
			})
			.catch((error) =>
			{
				setIsBusy(false)
				SiteService.displayApiError(error)
			})
	}
	
	const handleRequestShipping = (ownedVehicle) =>
	{
		setIsBusy(true)
		VehiclesService
			.addShippingRequest(ownedVehicle.vehicleId, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)
				
				trigger('raiseNotification', { title: 'Request Success', message: 'The shipping request was submitted.', type: 'success', duration: 5 })
				
				setPagingResult((previousPagingResult) =>
				{
					let currentPagingResult = previousPagingResult
					let pageItem = currentPagingResult.pageCollection.find((collectionItem) => collectionItem.vehicleId === ownedVehicle.vehicleId)
					if (pageItem)
					{
						pageItem.hasShippingRequest = true
					}
					return currentPagingResult
				})

				// Open move.openlane.com with origin address parameters
				const params = new URLSearchParams()
				if (ownedVehicle.serialNumber) params.set('vin', ownedVehicle.serialNumber)
				if (ownedVehicle.originStreet) params.set('orgstreet', ownedVehicle.originStreet)
				if (ownedVehicle.originCity) params.set('orgcity', ownedVehicle.originCity)
				if (ownedVehicle.originState) params.set('orgstate', ownedVehicle.originState)
				if (ownedVehicle.originZip) params.set('orgzip', ownedVehicle.originZip)
				window.open(`https://move.openlane.com/?${params.toString()}`, '_blank')
			})
			.catch((error) =>
			{
				setIsBusy(false)
				SiteService.displayApiError(error)
			})
	}
	
	const handleViewArbitrationInfo = () =>
	{
		setShowArbitrationModal(true)
	}
	
	const handleViewConfirmationOfSale = (ownedVehicle) =>
	{
		setIsBusy(true)
		DocumentsService
			.getBase64Document({ storeTypeId: Enumeration.StorageTypes.ConfirmationOfSales, fileName: ownedVehicle.englishConfirmationOfSaleFileName }, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsBusy(false)
				setConfirmationOfSaleBase64('data:application/pdf;base64,' + result.data.fileContent)
				
				let pdfWindow = window.open("")
				
				pdfWindow.document.write(
					"<iframe width='100%' height='100%' src='data:application/pdf;base64, " +
					encodeURI(result.data.fileContent) + "'></iframe>"
				)
			})
			.catch((error) =>
			{
				setIsBusy(false)
				SiteService.displayApiError(error)
			})
	}
	
	return (
		<>
			<SpinnerComponent
				loading={ isBusy }
				position="global"
				color="#3578e5"
				secondaryColor="#f3f3f3"
				backgroundColor="#d3d3d3"
				spinnerType="circle-dots"
			/>
			<PageTitleContextMenu
				heading={ t('OwnedVehicles.Listing.Title') }
				subheading=""
				icon="pe-7s-car icon-gradient bg-premium-dark"
				headerOptions={ { showBack: false, backCommand: null, showReload: true, reloadCommand: () => { handleReload() } } }
			/>
			<Container className="mt--6" fluid>
				<Row>
					<div className="col">
						<Card>
							<CardHeader className="border-0" style={{ paddingBottom: '1.5rem' }}>
								<Row className="align-items-end">
									<Col md="3">
										<FormGroup className="mb-0">
											<Label htmlFor="startDate">{ t('OwnedVehicles.Listing.StartDate', 'Start Date') }</Label>
											<Input id="startDate" name="startDate" type="date" value={ startDate } onChange={ (e) => setStartDate(e.target.value) } />
										</FormGroup>
									</Col>
									<Col md="3">
										<FormGroup className="mb-0">
											<Label htmlFor="endDate">{ t('OwnedVehicles.Listing.EndDate', 'End Date') }</Label>
											<Input id="endDate" name="endDate" type="date" value={ endDate } onChange={ (e) => setEndDate(e.target.value) } />
										</FormGroup>
									</Col>
									<Col md="3">
										<FormGroup className="mb-0">
											<Label>&nbsp;</Label>
											<div>
												<Button color="primary" onClick={ handleApplyDateFilter }>{ t('OwnedVehicles.Listing.ApplyFilter', 'Apply') }</Button>
											</div>
										</FormGroup>
									</Col>
								</Row>
							</CardHeader>
							<div className="table-responsive border-top" >
								<Table className="align-items-center table-flush">
									<thead className="thead-light">
										<tr>
											<th className="sort" data-sort="make" scope="col">{t('OwnedVehicles.Listing.Make')}</th>
											<th className="sort" data-sort="model" scope="col">{t('OwnedVehicles.Listing.Model')}</th>
											<th className="sort" data-sort="year" scope="col">{t('OwnedVehicles.Listing.Year')}</th>
											<th className="sort" data-sort="serialNumber" scope="col">{t('OwnedVehicles.Listing.Vin')}</th>
											<th className="sort" data-sort="mileage" scope="col">{t('OwnedVehicles.Listing.Mileage')}</th>
											<th className="sort" data-sort="type" scope="col">{t('OwnedVehicles.Listing.Type')}</th>
											<th className="sort" data-sort="amount" scope="col">{t('OwnedVehicles.Listing.Amount')}</th>
											<th className="sort" data-sort="ownershipRequest" scope="col">{t('OwnedVehicles.Listing.Ownership_Request')}</th>
											<th scope="col"/>
										</tr>
									</thead>
									<tbody className="list">
										{pagingResult.pageCollection.map((pageItem) => (
											<tr key={ pageItem.vehicleId }>
												<td>{ pageItem.make }</td>
												<td>{ pageItem.model }</td>
												<td>{ pageItem.year }</td>
												<td>{ pageItem.serialNumber }</td>
												<td>{ pageItem.mileage ? pageItem.mileage.toLocaleString() : 'N/A' }</td>
												<td>{ pageItem.purchaseOfferTypeName }</td>
												<td>{ pageItem.purchaseAmount ? pageItem.purchaseAmount.toLocaleString('en-US', {style:'currency', currency:'USD'}) : 'N/A' }</td>
												<td>{ pageItem.hasOwnershipRequest ? 'Yes' : 'No' }</td>
												<td className="text-right">
													<div>
														<Button
															className="btn-icon btn-icon-only btn-pill btn-outline-dark"
															style={{ cursor: 'pointer', marginLeft: '5px' }}
															color="secondary"
															title={ pageItem.hasOwnershipRequest ? "Ownership Request Pending" : "Request Ownership" }
															onClick={ () => handleRequestOwnership(pageItem) }
															disabled={ pageItem.hasOwnershipRequest }
														>
															<span className="btn-icon-wrapper">
																<FontAwesomeIcon icon={ faScroll } />
															</span>
														</Button>
														<Button
															className="btn-icon btn-icon-only btn-pill btn-outline-dark"
															style={{ cursor: 'pointer', marginLeft: '5px' }}
															color="secondary"
															title={ pageItem.hasShippingRequest ? "Shipping Requested - Click to request again" : "Request Shipping" }
															onClick={ () => handleRequestShipping(pageItem) }
														>
															<span className="btn-icon-wrapper">
																<FontAwesomeIcon icon={ faTruckRampBox } />
															</span>
														</Button>
														<Button
															className="btn-icon btn-icon-only btn-pill btn-outline-dark"
															style={{ cursor: 'pointer', marginLeft: '5px' }}
															color="secondary"
															title="Arbitration"
															onClick={ () => handleViewArbitrationInfo() }
														>
															<span className="btn-icon-wrapper">
																<FontAwesomeIcon icon={ faPeopleArrows } />
															</span>
														</Button>
														<Button
															className="btn-icon btn-icon-only btn-pill btn-outline-dark"
															style={{ cursor: 'pointer', marginLeft: '5px' }}
															color="secondary"
															title="Confirmation of Sale"
															onClick={ () => handleViewConfirmationOfSale(pageItem) }
														>
															<span className="btn-icon-wrapper">
																<FontAwesomeIcon icon={ faReceipt } />
															</span>
														</Button>
													</div>
												</td>
											</tr>
										))}
									</tbody>
								</Table>
							</div>
							<CardFooter className="py-4">
								{
									(pagingResult.pageCollection.length === 0) && (
										<Row style={{ marginBottom: '10px', width: '100%' }}>
											<Col md="12" style={{ textAlign: 'center' }}>
												<Label>{t('Vehicles.Listing.No_Results')}</Label>
											</Col>
										</Row>
									)
								}
								<nav aria-label="...">
									{
										(pagingResult.pageCollection.length > 0) && (
											<Paginator
												currentPage={ pagingRequest.pageIndex }
												pages={ parseInt(((pagingResult.totalRecords - 1) / pagingRequest.pageSize) + 1, 10) }
												selectPage={ handlePageChange }
											/>
										)
									}
								</nav>
							</CardFooter>
						</Card>
					</div>
				</Row>
			</Container>

			<Modal
				isOpen={ showArbitrationModal }
				toggle={ () => setShowArbitrationModal(false) }
			>
				<ModalHeader toggle={ () => setShowArbitrationModal(false) } charCode="Y">
					{ t('OwnedVehicles.Listing.Arbitration') }
				</ModalHeader>
				<ModalBody>
					<Card>
						<CardBody>
							<div className="modal-body">
								<p>Please email (info@valloraplatform.com) the following information and documents so the purchase complaint can be assessed</p>
								<ul>
									<li>Reason for purchase complaint</li>
									<li>Bill of lading</li>
									<li>Estimate for repair</li>
									<li>Photos of damage</li>
									<li>Transportation invoice</li>
								</ul>
								<p>Once received, purchase complaint will be reviewed and the decision will be referred back to the dealer.</p>
								<p>
									Sincerely,<br />
									Vallora
								</p>
							</div>
						</CardBody>
					</Card>
				</ModalBody>
				<ModalFooter>
					<Row style={{ width: '100%' }}>
						<Col md={12}>
							<Button
								block
								color="secondary"
								onClick={ () => setShowArbitrationModal(false) }
							>
								{ t('Common.Cancel') }
							</Button>
						</Col>
					</Row>
				</ModalFooter>
			</Modal>

			<PdfViewerModal
				showPdfViewerModal={ showPdfViewerModal }
				base64String={ confirmationOfSaleBase64 }
			/>
		</>
	)
}

export default DealerOwnedVehicleListing
